// services/counterpartyMemory.js — паметта по контрагент за ВСИЧКИ фирми
// (ПРОМТ-45, 02.11.2026).
//
// Защо: Виваком е „Телефон и интернет" във всяка от 100-те фирми, а 4216 ЕИК
// се срещат в повече от една фирма. Досегашната памет (services/expenseGuess.js:
// memoryFrom) е по фирма и по прочетен ред — нов доставчик във фирма B не
// знаеше нищо, макар фирма A да го е потвърждавала двайсет пъти. Ключът тук е
// ЕИК-ът на контрагента, не фирмата, а стойността е КАТЕГОРИЯТА (общият ключ
// за всички фирми, services/kontoCategories.js) — кодът на статията във
// фирмата се намира от картата при експорта, както досега.
//
// Редовете са два вида:
//   kind 'cat' — един на ЕИК и категория: { eik, category, account_group,
//                count, last_used }; count = в колко потвърдени доставки
//                категорията е била поне на един ред (една категория на
//                фактура — правилото за фирмите без склад);
//   kind 'doc' — какво е дала всяка доставка: { doc_id, eik, cats }, за да не
//                се брои два пъти при редакция и да се върне при триене.
// Доставката самата не се пипа — иначе всяко потвърждаване би оставило
// втори ред в одита.
//
// Не влизат: своите категории на фирма (own_*, чужди за другите фирми),
// контрагент без ЕИК и редовете със складов артикул (те имат артикул, не
// категория). Изключение по фирма (company.konto.purchases.memory_override:
// ЕИК → категория) печели над общата памет — Лукойл е „Гориво" навсякъде,
// но „Стоки" за фирмата-бензиностанция.

const COLL = 'counterparty_memory';
const MAX_ALTERNATIVES = 5;

// ЕИК за ключ: само цифри, без „BG" и без водещите нули (040058584 = 40058584,
// както firmKey в kontoSync). Под 9 цифри не е ЕИК.
function eikKey(v) {
  const d = String(v == null ? '' : v).toUpperCase().replace(/^BG/, '').replace(/\D/g, '');
  return d.length >= 9 ? d.replace(/^0+/, '') : '';
}

// Категориите на потвърдена доставка, които паметта приема: редове без
// артикул, с обща (не own_) категория. → Map категория → сметка.
function catsOf(doc) {
  const out = new Map();
  if (!doc || doc.status !== 'confirmed' || doc.advance) return out;
  for (const l of Array.isArray(doc.lines) ? doc.lines : []) {
    if (!l || l.article_id != null || !l.category) continue;
    const c = String(l.category);
    if (/^own_/.test(c)) continue;
    if (!out.has(c)) out.set(c, String(l.expense_account || ''));
  }
  return out;
}

function rowsOf(db, eik) {
  return db.all(COLL).filter((r) => r.kind === 'cat' && r.eik === eik);
}
function docRow(db, docId) {
  return db.all(COLL).find((r) => r.kind === 'doc' && Number(r.doc_id) === Number(docId)) || null;
}

function bump(db, eik, category, account, delta, when) {
  const row = db.all(COLL).find((r) => r.kind === 'cat' && r.eik === eik && r.category === category);
  if (!row) {
    if (delta > 0) db.insert(COLL, { company_id: null, kind: 'cat', eik, category, account_group: account || '', count: delta, last_used: when || '' });
    return;
  }
  const count = Math.max(0, (Number(row.count) || 0) + delta);
  if (!count) { db.remove(COLL, row.id); return; }
  const patch = { count };
  if (delta > 0) { patch.last_used = when && when > String(row.last_used || '') ? when : String(row.last_used || when || ''); if (account) patch.account_group = account; }
  db.update(COLL, row.id, patch);
}

// Доставката е потвърдена или потвърдената е редактирана: паметта се
// изравнява с това, което документът казва СЕГА (старото се връща, новото се
// добавя). contact: доставчикът (по него е ЕИК-ът). Вика се в транзакцията
// на записа. Връща { eik, cats } или null (доставката не дава памет).
function learn(db, doc, contact) {
  if (!doc || doc.id == null) return null;
  const prev = docRow(db, doc.id);
  const eik = eikKey(contact && (contact.eik || contact.vat_number));
  const cats = eik ? catsOf(doc) : new Map();
  const when = String(doc.confirmed_at || doc.updated_at || new Date().toISOString()).slice(0, 10);
  const next = cats.size ? { eik, cats: [...cats.keys()].sort() } : null;
  if (!prev && !next) return null;
  if (prev && next && prev.eik === next.eik && JSON.stringify((prev.cats || []).slice().sort()) === JSON.stringify(next.cats)) return next;
  if (prev) for (const c of prev.cats || []) bump(db, prev.eik, c, '', -1);
  for (const [c, acc] of cats) bump(db, eik, c, acc, +1, when);
  if (prev && next) db.update(COLL, prev.id, next);
  else if (prev) db.remove(COLL, prev.id);
  else db.insert(COLL, Object.assign({ company_id: null, kind: 'doc', doc_id: Number(doc.id) }, next));
  return next;
}

// Изтрита доставка: каквото е дала, се връща.
function forget(db, doc) {
  const prev = doc ? docRow(db, doc.id) : null;
  if (!prev) return;
  for (const c of prev.cats || []) bump(db, prev.eik, c, '', -1);
  db.remove(COLL, prev.id);
}

// Предложенията за доставчик във фирма: изключението на фирмата първо, после
// общата памет по честота (и по-скорошно ползване), до 5. available: Set от
// ключовете на категориите, които фирмата има (неархивирани); forOverride —
// същото за изключението (то е изрично и важи и там, където общата памет не).
// → [{ category, count, last_used, override? }]
function suggestions(db, company, eik, available, forOverride) {
  const key = eikKey(eik);
  if (!key) return [];
  const ok = (c) => !available || available.has(c);
  const okOv = (c) => (forOverride ? forOverride.has(c) : ok(c));
  const out = [];
  const ov = overrideOf(company, key);
  if (ov && okOv(ov)) out.push({ category: ov, count: 0, last_used: '', override: true });
  const rows = rowsOf(db, key).filter((r) => ok(r.category) && r.category !== ov)
    .sort((a, b) => (Number(b.count) || 0) - (Number(a.count) || 0) || String(b.last_used || '').localeCompare(String(a.last_used || '')));
  for (const r of rows) out.push({ category: r.category, count: Number(r.count) || 0, last_used: r.last_used || '' });
  return out.slice(0, MAX_ALTERNATIVES);
}

// Най-вероятната категория за ред без друга подсказка. Сигурна е (без жълто),
// когато е изключение на фирмата или когато всички потвърждения досега са
// единодушни и са поне две — иначе е само предложение (жълто).
function best(db, company, eik, available, forOverride) {
  const list = suggestions(db, company, eik, available, forOverride);
  if (!list.length) return null;
  const top = list[0];
  if (top.override) return { category: top.category, sure: true, how: 'override' };
  return { category: top.category, sure: list.length === 1 && top.count >= 2, how: 'shared' };
}

// company.konto.purchases.memory_override: { '<ЕИК>': 'категория' }.
const CAT_RE = /^[a-z0-9_]{1,40}$/;
function overrideOf(company, key) {
  const m = company && company.konto && company.konto.purchases && company.konto.purchases.memory_override;
  if (!m || typeof m !== 'object') return '';
  for (const [k, v] of Object.entries(m)) if (eikKey(k) === key && CAT_RE.test(String(v || ''))) return String(v);
  return '';
}
// Чистене на изключенията от заявката: ЕИК → ключ на категория, до 500.
function sanitizeOverrides(input) {
  const out = {};
  if (!input || typeof input !== 'object') return out;
  for (const [k, v] of Object.entries(input).slice(0, 500)) {
    const key = eikKey(k);
    const c = String(v || '').trim().toLowerCase();
    if (!key) throw new Error('Изключение за доставчик: „' + String(k).slice(0, 20) + '" не е ЕИК.');
    if (!CAT_RE.test(c)) throw new Error('Изключение за ЕИК ' + key + ': изберете категория.');
    out[key] = c;
  }
  return out;
}

// Еднократното пълнене от доставките, които вече са в CRM-а: паметта се
// строи наново от всички потвърдени доставки.
// Вика се при старта, ако паметта е празна. Връща броя на доставките, дали памет.
function rebuild(db) {
  let n = 0;
  db.transaction(() => {
    for (const r of db.all(COLL)) db.remove(COLL, r.id);
    const contacts = new Map(db.all('contacts').map((c) => [Number(c.id), c]));
    for (const d of db.all('purchase_docs')) {
      if (d.status !== 'confirmed') continue;
      if (learn(db, d, contacts.get(Number(d.contact_id)))) n++;
    }
  });
  return n;
}
function seedIfEmpty(db) {
  if (db.all(COLL).length) return 0;
  return rebuild(db);
}

module.exports = { COLL, eikKey, catsOf, learn, forget, suggestions, best, overrideOf, sanitizeOverrides, rebuild, seedIfEmpty, MAX_ALTERNATIVES };
